import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { apiGet } from "@/lib/api";
import { usePermitList } from "@/hooks/use-permits";
import { ActivePermitCard } from "@/components/permits/active-permit-card";
import { SearchableSelect } from "@/components/ui/searchable-select";

const permitsSearchSchema = z.object({
  status: z.string().optional(),
  type: z.string().optional(),
  project: z.string().optional(),
  page: z.number().int().positive().optional(),
});

export const Route = createFileRoute("/_authenticated/permits/")({
  validateSearch: permitsSearchSchema,
  component: PermitRegisterPage,
});

// ── Types ──────────────────────────────────────────────────────────────

interface Project {
  id: string;
  name: string;
}

// ── Component ──────────────────────────────────────────────────────────

function PermitRegisterPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const statusFilter = search.status ?? "active";
  const typeFilter = search.type ?? "all";
  const projectFilter = search.project ?? "all";
  const page = search.page ?? 1;
  const limit = 20;

  const setStatusFilter = (value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        status: value === "active" ? undefined : value,
        page: undefined,
      }),
      replace: true,
    });
  };
  const setTypeFilter = (value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        type: value === "all" ? undefined : value,
        page: undefined,
      }),
      replace: true,
    });
  };
  const setProjectFilter = (value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        project: value === "all" ? undefined : value,
        page: undefined,
      }),
      replace: true,
    });
  };
  const setPage = (updater: number | ((p: number) => number)) => {
    const next = typeof updater === "function" ? updater(page) : updater;
    navigate({
      search: (prev) => ({
        ...prev,
        page: next === 1 ? undefined : next,
      }),
      replace: true,
    });
  };

  // Fetch user's projects for filter dropdown
  const { data: projectsData } = useQuery({
    queryKey: ["projects"],
    queryFn: () =>
      apiGet<{ data: Project[] }>("/api/projects"),
  });
  const projects = projectsData?.data || [];

  // Fetch permits with current filters
  const { data: permitsData, isLoading } = usePermitList({
    status: statusFilter,
    permitType: typeFilter,
    projectId: projectFilter === "all" ? undefined : projectFilter,
    page,
    limit,
  });

  const permits = permitsData?.data || [];
  const total = permitsData?.total || 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      {/* Header */}
      <div className="pt-2">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
          <FileText className="h-6 w-6" />
          Permit Register
        </h1>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-3 gap-2">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="text-xs h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="text-xs h-9">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="hot_work">Hot Work</SelectItem>
            <SelectItem value="confined_space">Confined Space</SelectItem>
            <SelectItem value="excavation">Excavation</SelectItem>
            <SelectItem value="fall_protection">Fall Protection</SelectItem>
          </SelectContent>
        </Select>

        <SearchableSelect
          size="sm"
          className="h-9"
          value={projectFilter}
          onChange={(v) => { setProjectFilter(v); setPage(1); }}
          allLabel="All Projects"
          placeholder="Project"
          options={projects.map((p) => ({ value: p.id, label: p.name }))}
        />
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && permits.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="h-10 w-10 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">
              No permits found
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Permits are created from FLHA high-risk modules
            </p>
          </CardContent>
        </Card>
      )}

      {/* Permit list */}
      {!isLoading && permits.length > 0 && (
        <div className="space-y-2">
          {permits.map((permit) => (
            <ActivePermitCard key={permit.id} permit={permit} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="gap-1"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Prev
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="gap-1"
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
